import { useFormContext } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { Special } from '@common/entities';
import { useMutation } from '@tanstack/react-query';
import { NotificationStatus } from 'enums/notification-status';
import { resetSaveState, resetSaveTrigger, setSaveCompleted } from 'state/slices/actionsSlice';
import { setExternalSyncErrors, updateLeasingJourneyErrors } from 'state/slices/navigationSlice';
import { RootState } from 'state/store';

import notification from 'components/notification';
import { patchResponseForId } from 'services';
import { AddonOption } from 'types/addon';
import { buildPageRequestData, validatePropertyId } from 'utils/buildPageRequest';
import {
  excludeHiddenGuarantorErrors,
  excludeHiddenGuarantorResponse,
} from 'utils/display-logic/hiddenGuarantorResponses';
import { extractErrorMessage } from 'utils/extractErrorMessage';
import { getErrorCount } from 'utils/getErrors';
import { getDisplayableExternalSyncErrors } from 'utils/getExternalSyncErrors';
import handleError from 'utils/handleError';

import { useIsCountBasedGuarantorHidden } from '../display-logic/useIsCountBasedGuarantorHidden';
import { useGetPageData } from '../useGetPageData';
import { SaveResponseProps, useSaveResponse } from '../useSaveResponse';
import {
  getSecondaryApplicantSaveErrors,
  useSyncSecondaryApplicantsForPageSave,
} from '../useSyncSecondaryApplicantsForPageSave';

interface Props {
  activeStepIndex: number;
  responseId?: string;
}

export function useSaveOptionsPageData(props: Props) {
  const { activeStepIndex, responseId } = props;
  const {
    getFieldState,
    formState: { errors },
  } = useFormContext();

  const dispatch = useDispatch();
  const { form, activeResponse, applicant } = useSelector((state: RootState) => state.application);
  const { currentTabIndex } = useSelector((state: RootState) => state.navigation);
  const { isLeadProfile } = useSelector((state: RootState) => state.auth);
  const { saveTriggerCounter = 0 } = useSelector((state: RootState) => state.actions || {});
  const { pages } = form;
  const { applicationId } = activeResponse;
  const { propertyId } = applicant;

  const saveResponse = useSaveResponse();
  const getPageData = useGetPageData();
  const syncSecondaryApplicantsForPageSave = useSyncSecondaryApplicantsForPageSave();
  const isGuarantorHidden = useIsCountBasedGuarantorHidden(pages[activeStepIndex]?.id);

  const { mutateAsync: updateResponse } = useMutation({
    mutationFn: (data: SaveResponseProps) => saveResponse(data),
  });

  const { mutateAsync: updateResponseForId } = useMutation({
    mutationFn: patchResponseForId,
    onSuccess: () => {
      dispatch(setSaveCompleted(true));
      if (saveTriggerCounter > 0) {
        dispatch(resetSaveTrigger());
        notification('Save successful', NotificationStatus.SUCCESS);
      }
    },
    onError: (error) => {
      dispatch(resetSaveState());
      const errorMessage = extractErrorMessage(error);
      handleError(`Form Viewer - ${errorMessage}: ${error}`, `${errorMessage}`);
    },
  });

  async function saveOptionPageData(
    newPageIndex: number,
    addOns: AddonOption[],
    selectedSpecials?: Special[],
    validationErrorCount?: number,
  ) {
    if (!validatePropertyId(propertyId)) return false;
    try {
      const pageResponses = await syncSecondaryApplicantsForPageSave(
        excludeHiddenGuarantorResponse(getPageData(activeStepIndex), isGuarantorHidden),
        pages[activeStepIndex].id,
      );
      const errorCount = Math.max(
        validationErrorCount ?? 0,
        getErrorCount(
          excludeHiddenGuarantorErrors(
            getSecondaryApplicantSaveErrors(errors, getFieldState),
            isGuarantorHidden,
          ),
        ),
      );
      const { requestData } = buildPageRequestData({
        activeStepIndex,
        newPageIndex,
        pages,
        applicationId,
        propertyId,
        getPageResponses: () => pageResponses,
        errorCount,
        addOns,
        selectedSpecials,
      });

      let updateResult;
      if (responseId) {
        updateResult = await updateResponseForId({ responseId, responseData: requestData });
      } else {
        updateResult = await updateResponse(requestData);
      }

      const externalSyncErrors = getDisplayableExternalSyncErrors(
        updateResult?.data?.externalSync,
        errorCount,
      );
      dispatch(
        setExternalSyncErrors({
          tabIndex: isLeadProfile ? currentTabIndex : 0,
          pageId: requestData.currentPageId,
          errors: externalSyncErrors,
        }),
      );
      const syncErrorCount = externalSyncErrors.length > 0 ? Math.max(errorCount, 1) : errorCount;

      dispatch(
        updateLeasingJourneyErrors({
          pageIndex: activeStepIndex,
          count: syncErrorCount,
        }),
      );
      return true;
    } catch (error) {
      if (isLeadProfile) {
        dispatch(resetSaveState());
      }
      handleError(`Form Viewer - Error saving options: ${error}`, `Error saving options`);
      return false;
    }
  }

  return saveOptionPageData;
}
